import { useEffect, useState } from "react";
import {
  navigationService,
  NavItem,
  NavChild
} from "../services/navigationService";

// ─────────────────────────────────────────────
// Item Editor (Nine Sisters Design)
// ─────────────────────────────────────────────

function ItemEditor({
  item,
  onSave,
  onClose,
}: {
  item: Partial<NavItem>;
  onSave: (item: NavItem) => void;
  onClose: () => void;
}) {
  const isNew = !item.menuID;
  const [form, setForm] = useState<NavItem>({
    menuID: item.menuID ?? 0,
    menuName: item.menuName ?? "",
    menuUrl: item.menuUrl ?? "/",
    targetType: item.targetType ?? "_self",
    status: item.status ?? "Active",
    displayOrder: item.displayOrder ?? 99,
    isActive: item.isActive ?? true,
    navigationSubMenus: item.navigationSubMenus ?? [],
  });

  const [newChild, setNewChild] = useState<NavChild>({
    subMenuName: "",
    subMenuUrl: "/",
    targetType: "_self",
    status: "Active",
    displayOrder: 1,
    isActive: true,
  });

  function setF<K extends keyof NavItem>(key: K, value: NavItem[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function addChild() {
    if (!newChild.subMenuName.trim()) return;
    setForm(prev => ({
      ...prev,
      navigationSubMenus: [
        ...prev.navigationSubMenus,
        {
          ...newChild,
          displayOrder: prev.navigationSubMenus.length + 1,
        },
      ],
    }));
    setNewChild({
      subMenuName: "",
      subMenuUrl: "/",
      targetType: "_self",
      status: "Active",
      displayOrder: 1,
      isActive: true,
    });
  }

  function removeChild(index: number) {
    setForm(prev => ({
      ...prev,
      navigationSubMenus: prev.navigationSubMenus.filter((_, i) => i !== index),
    }));
  }

  return (
    <div className="mty-panel-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mty-panel">
        <div className="mty-panel-header">
          <div className="d-flex align-items-center gap-2">
            <div className="mst-icon-box"><i className="bi bi-menu-button-wide"></i></div>
            <div>
              <div className="fw-bold mst-title">{isNew ? "Add Menu Item" : `Edit — ${item.menuName}`}</div>
              <small className="mst-subtitle">Configure navigation link</small>
            </div>
          </div>
          <button type="button" className="btn-close" onClick={onClose} />
        </div>

        <div className="mty-panel-body">
          <div className="mty-section-label"><i className="bi bi-link-45deg me-2"></i>Menu Item</div>
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Label <span className="text-danger">*</span></label>
              <input className="form-control mst-control" placeholder="e.g. Jewellery"
                value={form.menuName} onChange={e => setF("menuName", e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">URL <span className="text-danger">*</span></label>
              <input className="form-control mst-control" placeholder="/jewellery"
                value={form.menuUrl} onChange={e => setF("menuUrl", e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold mst-label">Open In</label>
              <select className="form-select mst-select" value={form.targetType}
                onChange={e => setF("targetType", e.target.value as "_self" | "_blank")}>
                <option value="_self">Same Tab</option>
                <option value="_blank">New Tab</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold mst-label">Status</label>
              <select className="form-select mst-select" value={form.status}
                onChange={e => setF("status", e.target.value as "Active" | "Hidden")}>
                <option value="Active">Active</option>
                <option value="Hidden">Hidden</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold mst-label">Display Order</label>
              <input type="number" className="form-control mst-control" min={1}
                value={form.displayOrder} onChange={e => setF("displayOrder", Number(e.target.value))} />
            </div>
          </div>

          <div className="mty-section-label"><i className="bi bi-diagram-3 me-2"></i>Dropdown Sub-Items</div>
          <div className="mst-seo-hint mb-3"><i className="bi bi-info-circle me-1"></i>Add sub-links that appear in the dropdown menu under this item.</div>

          {form.navigationSubMenus.length > 0 && (
            <div className="nav-children-list mb-3">
              {form.navigationSubMenus.map((c, idx) => (
                <div key={idx} className="nav-child-row">
                  <div className="d-flex align-items-center gap-2 flex-1">
                    <i className="bi bi-grip-vertical nav-child-grip"></i>
                    <span className="fw-semibold" style={{ fontSize: 13 }}>{c.subMenuName}</span>
                    <code className="mst-code">{c.subMenuUrl}</code>
                    <span className={`badge ${c.status === "Active" ? "text-bg-success" : "text-bg-secondary"}`} style={{ fontSize: 10 }}>{c.status}</span>
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-danger mst-btn-delete"
                    onClick={() => removeChild(idx)}>
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="card border-0 mst-card" style={{ background: "var(--cream, #fdf8f3)" }}>
            <div className="card-body p-3">
              <div className="fw-semibold mst-label mb-2" style={{ fontSize: 12 }}>Add Sub-Item</div>
              <div className="row g-2">
                <div className="col-md-4">
                  <input className="form-control mst-control" placeholder="Label"
                    value={newChild.subMenuName} onChange={e => setNewChild(c => ({ ...c, subMenuName: e.target.value }))} />
                </div>
                <div className="col-md-4">
                  <input className="form-control mst-control" placeholder="/url"
                    value={newChild.subMenuUrl} onChange={e => setNewChild(c => ({ ...c, subMenuUrl: e.target.value }))} />
                </div>
                <div className="col-md-2">
                  <select className="form-select mst-select" value={newChild.status}
                    onChange={e => setNewChild(c => ({ ...c, status: e.target.value as "Active" | "Hidden" }))}>
                    <option value="Active">Active</option>
                    <option value="Hidden">Hidden</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <button type="button" className="btn btn-primary mst-btn-save w-100" onClick={addChild}
                    disabled={!newChild.subMenuName.trim()}>
                    <i className="bi bi-plus-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mty-panel-footer">
          <div />
          <div className="d-flex gap-2">
            <button type="button" className="btn mst-btn-reset" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary mst-btn-save"
              onClick={() => { onSave(form); onClose(); }}
              disabled={!form.menuName.trim() || !form.menuUrl.trim()}>
              <i className="bi bi-check-circle me-2"></i>{isNew ? "Add Item" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN PAGE (Nine Sisters Design)
// ─────────────────────────────────────────────

export function NavigationPage() {
  const [items, setItems] = useState<NavItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Partial<NavItem> | null>(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "danger">("success");
  const [saved, setSaved] = useState(false);

  // =========================================
  // LOAD DATA
  // =========================================
  async function loadMenus() {
    try {
      setLoading(true);
      const response = await navigationService.getAll();
      if (response.isSuccess) {
        setItems(response.dataList || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMenus();
  }, []);

  // =========================================
  // SAVE
  // =========================================
  async function handleSave(item: NavItem) {
    try {
      if (item.menuID && item.menuID > 0) {
        const response = await navigationService.update(item);
        setMessage(response.message || "Menu updated successfully");
      } else {
        const response = await navigationService.create(item);
        setMessage(response.message || "Menu created successfully");
      }
      setMessageType("success");
      setSaved(true);
      await loadMenus();
      setTimeout(() => {
        setSaved(false);
        setMessage("");
      }, 2500);
    } catch (error) {
      console.error(error);
      setMessage("Something went wrong");
      setMessageType("danger");
      setTimeout(() => {
        setMessage("");
      }, 3000);
    }
  }

  // =========================================
  // DELETE
  // =========================================
  async function handleDelete(id?: number) {
    if (!id) return;
    if (!window.confirm("Delete menu?")) return;
    try {
      const response = await navigationService.delete(id);
      setMessage(response.message || "Menu deleted successfully");
      setMessageType("success");
      await loadMenus();
      setTimeout(() => {
        setMessage("");
      }, 3000);
    } catch (error) {
      console.error(error);
      setMessage("Delete failed");
      setMessageType("danger");
      setTimeout(() => {
        setMessage("");
      }, 3000);
    }
  }

  // =========================================
  // MOVE ITEM (local order update)
  // =========================================
  function moveItem(id: number, dir: -1 | 1) {
    const sorted = [...items].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    const idx = sorted.findIndex(i => i.menuID === id);
    if ((dir === -1 && idx === 0) || (dir === 1 && idx === sorted.length - 1)) return;
    const swap = sorted[idx + dir];
    setItems(prev =>
      prev.map(i =>
        i.menuID === id ? { ...i, displayOrder: swap.displayOrder } :
        i.menuID === swap.menuID ? { ...i, displayOrder: sorted[idx].displayOrder } : i
      )
    );
  }

  // =========================================
  // TOGGLE STATUS
  // =========================================
  function toggleStatus(id: number) {
    setItems(prev =>
      prev.map(i =>
        i.menuID === id ? { ...i, status: i.status === "Active" ? "Hidden" : "Active" } : i
      )
    );
  }

  const sorted = [...items].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  return (
    <div className="d-grid gap-4 p-4">
      {/* Header card */}
      <div className="card border-0 shadow-sm mst-card">
        <div className="card-body p-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex align-items-center gap-2">
              <div className="mst-icon-box"><i className="bi bi-menu-button-wide"></i></div>
              <div>
                <h5 className="mb-0 fw-bold mst-title">Navigation Menu Manager</h5>
                <small className="mst-subtitle">Manage site header navigation links & dropdowns</small>
              </div>
            </div>
            <button type="button" className="btn btn-primary mst-btn-save"
              onClick={() => setEditing({})}>
              <i className="bi bi-plus-circle me-2"></i>Add Menu Item
            </button>
          </div>

          {(saved || message) && (
            <div className={`alert alert-${messageType || "success"} d-flex align-items-center gap-2 py-2 mt-3 mst-alert`}>
              <i className="bi bi-check-circle-fill"></i> {message || "Menu updated!"}
            </div>
          )}

          {/* Preview bar */}
          <div className="nav-preview-bar mt-4">
            <span className="nav-preview-brand">NineSisters</span>
            {sorted.filter(i => i.status === "Active").map(i => (
              <span key={i.menuID} className="nav-preview-link">
                {i.menuName}
                {i.navigationSubMenus?.filter(c => c.status === "Active").length > 0 && (
                  <i className="bi bi-chevron-down ms-1" style={{ fontSize: 9 }}></i>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
        </div>
      ) : (
        <div className="d-grid gap-2">
          {sorted.map((item, idx) => (
            <div key={item.menuID}
              className={`card border-0 shadow-sm mst-card nav-item-card ${item.status === "Hidden" ? "opacity-50" : ""}`}>
              <div className="card-body p-3">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  {/* Order controls */}
                  <div className="d-flex flex-column gap-1">
                    <button type="button" className="btn btn-sm mst-btn-reset p-1 lh-1"
                      onClick={() => moveItem(item.menuID!, -1)} disabled={idx === 0}>
                      <i className="bi bi-chevron-up" style={{ fontSize: 11 }}></i>
                    </button>
                    <button type="button" className="btn btn-sm mst-btn-reset p-1 lh-1"
                      onClick={() => moveItem(item.menuID!, 1)} disabled={idx === sorted.length - 1}>
                      <i className="bi bi-chevron-down" style={{ fontSize: 11 }}></i>
                    </button>
                  </div>

                  {/* Order badge */}
                  <span className="nav-order-badge">{item.displayOrder}</span>

                  {/* Label & URL */}
                  <div className="flex-grow-1">
                    <div className="fw-bold" style={{ fontSize: 14 }}>{item.menuName}</div>
                    <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                      <code className="mst-code">{item.menuUrl}</code>
                      {item.targetType === "_blank" && (
                        <span className="badge text-bg-secondary" style={{ fontSize: 10 }}>
                          <i className="bi bi-box-arrow-up-right me-1"></i>New Tab
                        </span>
                      )}
                      {item.navigationSubMenus && item.navigationSubMenus.length > 0 && (
                        <span className="badge text-bg-secondary" style={{ fontSize: 10 }}>
                          <i className="bi bi-diagram-3 me-1"></i>{item.navigationSubMenus.length} sub-items
                        </span>
                      )}
                    </div>
                    {/* Sub-items preview */}
                    {item.navigationSubMenus && item.navigationSubMenus.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-2">
                        {item.navigationSubMenus.map((c, childIdx) => (
                          <span key={childIdx}
                            className={`badge ${c.status === "Active" ? "text-bg-light border" : "text-bg-secondary"}`}
                            style={{ fontSize: 11, fontWeight: 400 }}>
                            {c.subMenuName}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Status badge */}
                  <span className={`badge ${item.status === "Active" ? "text-bg-success" : "text-bg-secondary"}`}>
                    {item.status}
                  </span>

                  {/* Actions */}
                  <div className="pg-actions">
                    <button type="button" className="btn btn-sm btn-outline-primary mst-btn-status"
                      onClick={() => setEditing(item)} title="Edit">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary mst-btn-status"
                      onClick={() => toggleStatus(item.menuID!)}>
                      {item.status === "Active" ? "Hide" : "Show"}
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-danger mst-btn-delete"
                      onClick={() => handleDelete(item.menuID)}>
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <ItemEditor
          item={editing}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}